/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel, { CampaignUpdateTypes, Edge } from 'cio/models/campaign';
import { action, computed, setProperties } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { inject as service } from '@ember/service';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import { next, scheduleOnce, later } from '@ember/runloop';
import { task, Task, waitForEvent } from 'ember-concurrency';
import { notEmpty, or } from '@ember/object/computed';
import { isNone } from '@ember/utils';

import { processWorkflowGraph, reprocessWorkflowGraphSizes } from 'cio/utils/visual-workflow-builder/layout';
import { prepareGraphNodeForAction } from 'cio/utils/visual-workflow-builder/layout/preprocess';
import { createActionAbstract } from 'cio/utils/visual-workflow-builder/action-abstracts';

import AnalyticsService from 'cio/services/analytics';

import {
  SIDEBAR_WIDTH,
  SIDEBAR_BOX_SHADOW_BLUR,
  KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY,
  IN_VIEWPORT_CAMPAIGN_SIZE_THRESHOLD,
  ADD_ACTIONS_PANEL_WIDTH,
} from 'cio/utils/visual-workflow-builder';
import { getNodeIdsWithoutIncomingEdges } from 'cio/utils/visual-workflow-builder/branching';
// @ts-ignore
import move from 'ember-animated/motions/move';
// @ts-ignore
import { fadeIn, fadeOut } from 'ember-animated/motions/opacity';
// @ts-ignore
import { easeIn, easeOut } from 'ember-animated/easings/cosine';
import Sprite from 'ember-animated/-private/sprite';
import ActionModel from 'cio/models/action';

import { WorkflowGraph, WorkflowGraphPath } from 'cio/utils/visual-workflow-builder/workflow-graph';
import EventBusService, {
  VWB_ACTION_UPDATED,
  VWB_ACTION_STATE_CHANGED,
  VWB_BEFORE_ACTION_ADD_MOVE_OR_DELETE,
  VWB_CANCEL_MOVE,
} from 'cio/services/event-bus';
import { estimateSize } from 'cio/utils/visual-workflow-builder/layout/size';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from 'cio/services/account';
import RavenService from 'cio/services/raven';

import config from 'cio/config/environment';
import { logTimeElapsed } from 'cio/utils/time';

import isDestroyed from 'cio/utils/is-destroyed';

import { storageFor } from 'ember-local-storage';
import EnvironmentService from 'cio/services/environment';
import DS from 'ember-data';

import { DropType, DragMode, MouseMode, VWBTab, DropTargetItem } from 'cio/utils/visual-workflow-builder/types';
import { Periods } from 'cio/utils/metrics/common';
import { TimeRange } from 'cio/utils/time';
import {
  bulkDelete,
  prepareEdgesForBulkCopy,
  connectNodesIntoSubGraph,
  prepareInsertAtEdges,
  prepareExpandedEdgesForBulkCopy,
} from 'cio/utils/visual-workflow-builder/bulk-actions';

import {
  addConditionsToChildActions,
  addWarningsToActions,
  updateActionsBasedOnWarnings,
} from 'cio/utils/visual-workflow-builder/copying';
import RouterService from '@ember/routing/router-service';
import ApiService from 'cio/services/api';

import isEqual from 'lodash.isequal';

import Ember from 'ember';
import ActionSubjectsService from 'cio/services/action-subjects';

import { VWB_CONFIRM_BULK_MOVE, VWB_CONFIRM_SINGLE_MOVE } from 'cio/services/event-bus';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { WorkflowCopyModalActions, WorkflowCopyModalAnalyticsEvents } from 'cio/utils/analytics/transforms/campaign';
import { Events, FlyDragEvent } from 'fly-drag-drop/utils/events';
import FlyDragDropManager from 'fly-drag-drop/services/fly-drag-drop-manager';

import { buildWaiter } from '@ember/test-waiters';
import AggressivePositivityService from 'cio/services/aggressive-positivity';
import AuthService from 'cio/services/auth';
import { getActionIdsInConsolidatedGroup } from 'cio/utils/campaigns/tree-consolidation';
import { createStickyNotesManagerForComponent, StickyNotesManager } from 'cio/utils/visual-workflow-builder/notes';
import { createZoomManagerForComponent, ZoomManager } from 'cio/utils/visual-workflow-builder/zoom';
import { createInertiaManagerForComponent, InertiaManager } from 'cio/utils/visual-workflow-builder/inertia';
import { VWBContext } from 'cio/utils/visual-workflow-builder/context';
import { AddActionsPanelItemConfigs } from 'cio/utils/visual-workflow-builder/add-actions-panel';

let mouseMoveWaiter = buildWaiter('vwb:mouse-move-waiter');

const SIDEBAR_TRANSLATE_OFFSET = SIDEBAR_WIDTH + SIDEBAR_BOX_SHADOW_BLUR;

// const SHOULD_LOG = config.isProduction ? false : true;
const SHOULD_LOG = false;

type AnyActionAbstract = {
  type: string;
  actionType: string;
  action: ActionModel;
  isDirty: boolean;
};

interface VisualWorkflowBuilderV2Args {
  campaign: CampaignModel;
  isReadOnly: boolean;
  isCampaignOverview: boolean;
  isCampaignReview: boolean;
  isCampaignSetup: boolean;
  tab: VWBTab;
  metricsTabSelectedMeasures: string[];
  selectMetricsTabMeasure: (measure: string) => unknown;
  deselectMetricsTabMeasure: (measure: string) => unknown;
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
  changeMetricsTabTimePeriod: (period: Periods) => unknown;
  changeMetricsTabTimeRange: (range: TimeRange) => unknown;
  changeTab: (tab: VWBTab) => unknown;
  selectedActionAbstract: AnyActionAbstract;
  addActionTask: Task<
    unknown[],
    [
      DropType,
      any,
      FlyDragEvent,
      { branch?: any; branchOutgoingEdges?: any; copyFromEnv: string; copy: boolean; newEdges?: Edge[] },
    ]
  >;
  onSelectedAction: (actionAbstract: AnyActionAbstract) => unknown;
  onActionDestroyed: () => unknown;
  isCopyingFrom?: boolean;
  highlightedNodeIds: string[];
  updateHighlightedNodeIds: (highlightedNodeIds: string[]) => unknown;
  onCanPerformHighlightOperationChanged?: ({
    canCopy,
    canMove,
    canDelete,
  }: {
    canCopy: boolean;
    canMove: boolean;
    canDelete: boolean;
  }) => unknown;
  isCopyingTo?: boolean;
  beforeActionIds?: string[];
  onCopyToFinished?: (serializedCampaign: any, serializedActions: any[]) => unknown;
  copyToSourceCampaign?: CampaignModel;
  copyToTargetEnvironmentId?: string;
  onUndo?: () => unknown;
  isMinimapMode?: boolean;
  selectedMinimapAction?: string;
  // Zoom level at first render
  initialZoomLevel?: number;
  minZoomLevel?: number;
  maxZoomLevel?: number;
  centerRelativeToSidePanelOnInitialLoad?: boolean;
  demoMode?: boolean;
  stickyNotesEnabled?: boolean;

  /**
    Allows configuring items in the add actions panel. The possible
    customization options are:
    - showing a label
    - showing a popover

    @argument addPanelItemConfigs
    @type AddActionsPanelItemConfigs
    @optional
   */
  addPanelItemConfigs?: AddActionsPanelItemConfigs;
}

let reduceWithBranchChildren = (campaign: CampaignModel, acc: string[], id: string) => {
  let parsedBranch = campaign.branchesMap[id];

  acc = [...acc, id];

  if (!parsedBranch) {
    return acc;
  }

  let branchActionModel = parsedBranch.action;

  acc = [...acc, ...branchActionModel.subActions];

  return acc;
};

const AUTOSCROLL_THRESHOLD = 100;
const AUTOSCROLL_SPEED = 15;

export default class VisualWorkflowBuilderV2 extends Component<VisualWorkflowBuilderV2Args> {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service actionSubjects!: ActionSubjectsService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service aggressivePositivity!: AggressivePositivityService;
  @service eventBus!: EventBusService;
  @service features!: FeaturesService;
  @service flyDragDropManager!: FlyDragDropManager;
  @service raven!: RavenService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;
  @service environment!: EnvironmentService;

  @storageFor('cio-alerts') localStorage!: any;

  @tracked loaded = false;
  @tracked inViewport = false;

  rootElement: HTMLDivElement | null = null;
  @tracked canvas: HTMLDivElement | null = null;
  @tracked graphElement: HTMLDivElement | null = null;

  @tracked buildTabGraph: WorkflowGraph | null = null;
  @tracked metricTabGraph: WorkflowGraph | null = null;

  @tracked currentGraph: WorkflowGraph | null = null;

  @tracked oldPathsMap: Record<string, any> = {};
  @tracked pathsMap: Record<string, any> = {};

  @tracked offsetX = 0;
  @tracked offsetY = 0;
  @tracked pathStrokeWidth = 1;

  @tracked mouseMode: MouseMode = MouseMode.Highlight;

  @tracked isSelectingDropAreaThroughClick = false;
  @tracked isShowingCopyFromBanner = false;
  @tracked isShowingCopyToBanner = false;

  @tracked isPerformingDragOperation = false;
  @tracked selectedAction: ActionModel | null = null;

  @or(
    'flyDragDropManager.isDragging',
    'isSelectingDropAreaThroughClick',
    'isMovingHighlightedActions',
    'isCopyingHighlightedActions',
    'isPerformingDragOperation',
  )
  shouldAnimateNearbyActions!: boolean;

  @computed(`localStorage.${KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY}`, 'demoMode')
  get showZoomHintPopupOnClick() {
    return !this.localStorage.get(KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY) && !this.demoMode;
  }

  get stickyNotesEnabled() {
    return this.args.stickyNotesEnabled ?? true;
  }

  get width() {
    return this.currentGraph?.width ?? 0;
  }
  get height() {
    return this.currentGraph?.height ?? 0;
  }
  get nodes() {
    return Object.values(this.currentGraph?.nodes ?? {});
  }
  get paths() {
    return this.currentGraph?.paths ?? [];
  }

  get containerCssClasses() {
    let classes: string[] = [];

    if (this.args.isCampaignReview) {
      classes.push('is-campaign-review');
    }

    let scale = this.zoomManager.zoomScale;
    if (scale < 0.25) {
      classes.push('should-hide-bg');
    }

    if (this.args.isMinimapMode === true) {
      classes.push('minimap');

      if (scale < 1) {
        classes.push('high-level');
      }
    }

    return classes.join(' ');
  }

  get mainStyles() {
    return htmlSafe(`
--scale: ${this.zoomManager.zoomScale};
--width: ${this.width}px;
--height: ${this.height}px;
--offsetX: ${this.offsetX}px;
--offsetY: ${this.offsetY}px;
--pathStrokeWidth: ${this.pathStrokeWidth}px;
`);
  }

  get canClickNodes() {
    if (this.mouseMode === MouseMode.Scroll) {
      // When switching to the metrics tab, the mouse mode is set to "scroll",
      // but we still want to allow clicking on nodes
      return this.args.tab === VWBTab.Metrics;
    }

    return this.mouseMode === MouseMode.Highlight;
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign.isApiTriggered === true) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  stickyNotesManager: StickyNotesManager;
  zoomManager: ZoomManager;
  inertiaManager: InertiaManager;
  context: VWBContext;

  constructor(owner: unknown, args: VisualWorkflowBuilderV2Args) {
    super(owner, args);

    this.pathTransition = this.pathTransition.bind(this);
    this.nodeTransition = this.nodeTransition.bind(this);

    if (!this.args.isCopyingFrom && this.args.isReadOnly) {
      this.mouseMode = MouseMode.Scroll;
    }

    if (this.args.isCopyingTo) {
      this.isSelectingDropAreaThroughClick = true;
      this.isCopyingHighlightedActions = true;
      this.isShowingCopyToBanner = true;
    }

    if (this.args.isCopyingFrom) {
      this.isShowingCopyFromBanner = true;
    }

    // If we're in testing mode, set "inViewport" to true right away, so that
    // the VWB contents get rendered immediately.
    // This is necessary because the test container may sometimes render off
    // screen, and in that case the VWB's contents wouldn't render at all,
    // leading to confusing failures.
    if (Ember.testing) {
      this.inViewport = true;
    }

    let stickyNotesManager = createStickyNotesManagerForComponent(this);
    let zoomManager = createZoomManagerForComponent(this);
    let inertiaManager = createInertiaManagerForComponent(this);
    this.stickyNotesManager = stickyNotesManager;
    this.zoomManager = zoomManager;
    this.inertiaManager = inertiaManager;

    this.context = {
      stickyNotesManager,
      zoomManager,
      inertiaManager,
    };
  }

  @action
  didInsert(rootElement: HTMLDivElement) {
    this.rootElement = rootElement;
    this.flyDragDropManager.on(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.on(Events.DragEnd, this.onDragEnd);

    this.processWorkflowGraph();

    this.visualWorkflowBuilder.registerVWBComponent(this);

    this.eventBus.subscribe(VWB_ACTION_UPDATED, this, this.onActionUpdated);
    this.eventBus.subscribe(VWB_ACTION_STATE_CHANGED, this, this.onActionStateChanged);

    if (this.args.isMinimapMode !== true) {
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('keyup', this.onKeyUp);
    }
  }

  willDestroy() {
    super.willDestroy();
    this.visualWorkflowBuilder.unregisterVWBComponent(this);

    this.eventBus.unsubscribe(VWB_ACTION_UPDATED, this, this.onActionUpdated);
    this.eventBus.unsubscribe(VWB_ACTION_STATE_CHANGED, this, this.onActionStateChanged);

    if (this.args.isMinimapMode !== true) {
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('keyup', this.onKeyUp);
    }

    this.flyDragDropManager.off(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.off(Events.DragEnd, this.onDragEnd);
  }

  @action
  didInsertCanvas(element: HTMLDivElement) {
    this.canvas = element;
    this.zoomManager.registerCanvasEvents();
    this.inertiaManager.registerCanvasEvents();

    this.updateOffsets(this.zoomManager.zoomScale);
  }

  @action
  didInsertGraphElement(element: HTMLDivElement) {
    this.graphElement = element;
  }

  @action
  updateOffsets(targetScale: number) {
    if (!this.canvas) {
      return;
    }

    let targetWidth = this.width * targetScale;
    let canvasWidth = this.canvas.offsetWidth;

    let widthDiff = Math.abs(targetWidth - canvasWidth);

    let offsetX = Math.max(widthDiff / 2, SIDEBAR_WIDTH + SIDEBAR_BOX_SHADOW_BLUR);

    this.offsetX = offsetX;
    this.offsetY = this.canvas.offsetHeight / 2;
  }

  @action
  onActionUpdated(actionModel: ActionModel) {
    this.reprocessGraphIfNecessary(actionModel);

    // Reset the "undo last operation" state, because that only applies to
    // adding/moving actions, not making edits to an action
    this.previousEdges = null;
    this.previousActionIds = null;
  }

  @action
  onActionStateChanged(actionModel: ActionModel) {
    this.reprocessGraphIfNecessary(actionModel);
  }

  @action
  reprocessGraphIfNecessary(actionModel: ActionModel) {
    let { currentGraph } = this;

    if (!currentGraph) {
      return;
    }

    let currentActionNode = Object.values(currentGraph.nodes).findBy('id', actionModel.id);

    if (!currentActionNode) {
      return;
    }

    let currentSize = {
      width: currentActionNode.width,
      height: currentActionNode.height,
    };

    let newSize = estimateSize(prepareGraphNodeForAction(actionModel), this.args.tab, this.args.campaign);

    if (newSize.height !== currentSize.height || newSize.width !== currentSize.width) {
      if (this.args.tab === VWBTab.Build) {
        this.metricTabGraph = null;
        this.buildTabGraph = logTimeElapsed(
          'reprocess graph',
          () =>
            reprocessWorkflowGraphSizes(
              currentGraph!,
              this.args.tab,
              this.features.isEnabled('vwb_unstable_algorithm'),
            ),
          SHOULD_LOG,
        );
        this.currentGraph = this.buildTabGraph;
      } else {
        this.buildTabGraph = null;
        this.metricTabGraph = logTimeElapsed(
          'reprocess graph',
          () =>
            reprocessWorkflowGraphSizes(
              currentGraph!,
              this.args.tab,
              this.features.isEnabled('vwb_unstable_algorithm'),
            ),
          SHOULD_LOG,
        );
        this.currentGraph = this.metricTabGraph;
      }
    }
  }

  @action
  onTabChanged() {
    this.loaded = false;
    next(this, this.processWorkflowGraph);

    if (this.args.tab === VWBTab.Metrics) {
      this.mouseMode = MouseMode.Scroll;
    } else {
      this.mouseMode = MouseMode.Highlight;
    }
  }

  lastProcessedEdges: Edge[] | null = null;
  lastProcessedConsolidatedTreeEdges: Edge[] | null = null;
  get edgesChangedSinceLastProcessed() {
    let { campaign } = this.args;

    let eqEdges = isEqual(campaign.edges, this.lastProcessedEdges);
    let eqConsolidatedTreeEdges = isEqual(campaign.consolidatedTree.edges, this.lastProcessedConsolidatedTreeEdges);

    return !eqEdges || !eqConsolidatedTreeEdges;
  }

  @action
  reprocess() {
    this.buildTabGraph = null;
    this.metricTabGraph = null;
    this.currentGraph = null;

    this.processWorkflowGraph();

    // Check if all highlighted nodes still exist - it's possible that some
    // nodes have been deleted, which causes reprocess to run. In this case we
    // need to remove the removed nodes from the highlighted array
    if (this.currentlyHighlightedNodeIds.length > 0) {
      let notDeletedActionIds = this.args.campaign.consolidatedTree.actions.map((a) => a.id);

      let onlyStillExistingActionIds = this.currentlyHighlightedNodeIds.filter((id) =>
        notDeletedActionIds.includes(id),
      );
      if (onlyStillExistingActionIds.length !== this.currentlyHighlightedNodeIds.length) {
        this.args.updateHighlightedNodeIds([...onlyStillExistingActionIds]);
        this.hasChangedHighlight();
      }
    }
  }

  @action
  processWorkflowGraph() {
    if (this.args.tab === VWBTab.Build && !isNone(this.buildTabGraph)) {
      this.currentGraph = this.buildTabGraph;
      this.loaded = true;

      return;
    } else if (this.args.tab === VWBTab.Metrics && !isNone(this.metricTabGraph)) {
      this.currentGraph = this.metricTabGraph;
      this.loaded = true;

      return;
    }

    let graph = logTimeElapsed(
      'process graph',
      () => processWorkflowGraph(this.args.campaign, this.args.tab, this.features.isEnabled('vwb_unstable_algorithm')),
      SHOULD_LOG,
    );

    if (this.args.tab === VWBTab.Build) {
      this.buildTabGraph = graph;
    } else {
      this.metricTabGraph = graph;
    }

    this.currentGraph = graph;

    if (Object.keys(this.pathsMap).length > 0) {
      this.oldPathsMap = this.pathsMap;
    }
    this.pathsMap = graph.paths.reduce<Record<string, WorkflowGraphPath>>((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});

    next(this, () => {
      this.loaded = true;
    });
  }

  @action
  centerCanvas() {
    if (isDestroyed(this)) {
      return;
    }
    let scrollLeft = 0;
    let scrollTop = 0;

    if (this.args.selectedActionAbstract || this.args.selectedMinimapAction != null) {
      let coordinates = this.getActionCanvasCoordinatesFromIdOrModel(
        this.args.selectedMinimapAction,
        this.args.selectedActionAbstract?.action,
      );
      scrollLeft = coordinates.scrollLeft;
      scrollTop = coordinates.scrollTop;
    } else {
      let enterNode = this.nodes.find(
        (node) => node.type === 'virtual_enter' || node.type === 'filter_match_delay_action',
      );
      if (enterNode) {
        scrollLeft = enterNode.x * this.zoomManager.zoomScale;
        scrollTop = enterNode.y + this.offsetY / 2;

        if (this.args.centerRelativeToSidePanelOnInitialLoad) {
          scrollLeft = scrollLeft - ADD_ACTIONS_PANEL_WIDTH / 2;
        }
      }
    }

    if (!this.canvas) {
      return;
    }
    this.canvas.scrollLeft = scrollLeft;
    this.canvas.scrollTop = scrollTop;
  }

  getActionCanvasCoordinatesFromIdOrModel(actionId?: string, action?: ActionModel) {
    let coordinates = {
      scrollLeft: 0,
      scrollTop: 0,
    };

    if (actionId == null && action == null) {
      return coordinates;
    }

    if (actionId == null && action != null) {
      actionId = action.id;
    } else if (actionId != null && action == null) {
      action = this.args.campaign.actions.findBy('id', actionId);
    }

    if (action?.isVariation) {
      // if the selected action is a split's variation, it won't have
      // coordinates due to being embedded in the parent split's DOM node.
      // So we'll scroll to the parent split instead.
      actionId = action.belongsTo('parentAction').id();
    }

    let selectedNode = this.nodes.findBy('id', actionId);

    if (selectedNode) {
      let scale = this.zoomManager.zoomScale;
      if (this.args.isMinimapMode === true) {
        coordinates.scrollLeft = selectedNode.x * scale + this.offsetX / 3;
        coordinates.scrollTop = selectedNode.y + scale * 3;
      } else {
        coordinates.scrollLeft = selectedNode.x * scale - SIDEBAR_WIDTH / 2;
        coordinates.scrollTop = selectedNode.y + this.offsetY / 2;
      }
    }

    return coordinates;
  }

  get isHighlighting() {
    return (
      this.mouseMode === MouseMode.Highlight &&
      (this.args.isCopyingFrom ? true : !this.args.isReadOnly) &&
      this.args.tab !== VWBTab.Metrics
    );
  }

  @action
  onKeyDown(event: FlyKeyboardEvent<HTMLElement>) {
    if (this.isShowingCopyFromModal || this.isShowingCopyToModal) {
      return;
    }

    if (event.key === ' ') {
      if (this.mouseMode === MouseMode.Highlight) {
        this.mouseMode = MouseMode.Scroll;
      }

      // Don't preventDefault when we're in an input, so that editing text can
      // still happen freely
      if (event.target && !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === 'Alt') {
      this.isAltHeld = true;

      if (
        !this.flyDragDropManager.isDragging &&
        !this.args.selectedActionAbstract &&
        !this.args.isCopyingTo &&
        !this.args.isReadOnly
      ) {
        this.changeDragMode(DragMode.Copy);
      }
    }

    if (event.key === 'Shift') {
      this.isShiftHeld = true;
    }

    // shift + n
    // show/hide notes shortcut
    if (this.isShiftHeld && ['n', 'N'].includes(event.key) && !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
      this.stickyNotesManager.toggleStickyNotes();
    }

    // The below is for handling zooming in/out via keyboard shortcuts
    //
    // This handles the following keyboard shortcuts:
    // cmd +
    // cmd -
    // ctrl +
    // ctrl -
    if (!(event.metaKey || event.ctrlKey)) {
      return;
    }

    // "+" lives on the same key as "=", but requires a shift. As a shortcut,
    // shift is not used
    if (event.key === '=') {
      event.preventDefault();
      this.localStorage.set(KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY, true);
      return this.zoomManager.zoomIn('keyboard');
    }

    if (event.key === '-') {
      event.preventDefault();
      this.localStorage.set(KEYBOARD_SHORTCUT_LOCAL_STORAGE_KEY, true);
      return this.zoomManager.zoomOut('keyboard');
    }
  }

  @action
  onKeyUp(event: FlyKeyboardEvent<HTMLElement>) {
    if (
      this.args.tab === VWBTab.Build && !this.args.isCopyingTo && this.args.isCopyingFrom ? true : !this.args.isReadOnly
    ) {
      this.mouseMode = MouseMode.Highlight;
    } else {
      this.mouseMode = MouseMode.Scroll;
    }

    if (!this.isMouseDown && !this.flyDragDropManager.isDragging && !this.args.isReadOnly) {
      this.changeDragMode(DragMode.Move);
    }

    if (event.key === 'Alt') {
      this.isAltHeld = false;
    }

    if (event.key === 'Shift') {
      this.isShiftHeld = false;
    }

    // Show the multiselect delete modal if there is a selection and Delete or
    // Backspace is pressed.
    // This doesn't work when the VWB is rendered in one of the copy modals,
    // since it's readonly there anyway
    // Ignore the delete key if we're within an input (as that means we're
    // typing something)
    if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      !['INPUT', 'TEXTAREA'].includes(event.target.tagName) &&
      this.hasHighlightedNodes &&
      !this.args.isReadOnly &&
      !this.args.isCopyingFrom &&
      !this.args.isCopyingTo &&
      this.canPerformDelete &&
      !this.args.selectedActionAbstract
    ) {
      this.startHighlightDelete('keyboard');
    }

    // If Escape is pressed and there is an edit sidebar open, the sidebar will
    // attempt to close the sidebar (it may not close if there are unsaved
    // changes, it'll show a confirmation modal first). The sidebar has its own
    // key listener for that.
    // If there is no edit sidebar but there are highlights, unhighlight all
    // Clear current highlight when Escape is pressed
    if (
      event.key === 'Escape' &&
      !this.isShowingCopyFromModal &&
      !this.isShowingCopyToModal &&
      !this.args.selectedActionAbstract &&
      this.hasHighlightedNodes
    ) {
      if (!this.isSelectingDropAreaThroughClick) {
        this.args.updateHighlightedNodeIds([]);
      }
      this.dismissHighlightPanel();
      this.hasChangedHighlight();
    }
  }

  @tracked startDragScrollCoordinates: any = { x: 0, y: 0 };
  @tracked startScrollPosition: any = { x: 0, y: 0 };
  @tracked isMouseDown = false;
  @tracked isShiftHeld = false;
  @tracked isAltHeld = false;

  @tracked selectionRectCoordinates = {
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  };

  @tracked intersectingNodeIds: string[] = [];

  get externalHighlightedNodeIds() {
    return this.args.highlightedNodeIds ?? [];
  }

  // When performing a shift+highlight, we need to compare the previous
  // selection to the current one. Any already highlighted nodes that are being
  // selected should be deselected, while previously not selected nodes should
  // get selected.
  // This is why during a highlight we refer to this getter as the current
  // highlighted node ids.
  get highlightedNodeIdsWhileHighlighting() {
    let highlightedAndIntersectingNodes = [...this.intersectingNodeIds, ...this.externalHighlightedNodeIds].uniq();
    let currentlyHighlightedNodesWithinIntersection = this.externalHighlightedNodeIds.filter((id) =>
      this.intersectingNodeIds.includes(id),
    );

    // If a branch action is already highlighted and is currently within the
    // selection rectangle, remove it from the highlighted nodes, along with all
    // its child actions
    let nodeIdsToRemove = currentlyHighlightedNodesWithinIntersection
      .reduce(reduceWithBranchChildren.bind(this, this.args.campaign), [] as string[])
      .uniq();

    // For any highlighted branch action, make sure all its children are also
    // being highlighted
    let highlightedNodeIds = highlightedAndIntersectingNodes
      .filter((id) => !nodeIdsToRemove.includes(id))
      .reduce(reduceWithBranchChildren.bind(this, this.args.campaign), [] as string[])
      .uniq();

    return highlightedNodeIds;
  }

  get currentlyHighlightedNodeIds() {
    return this.isMouseDown && this.isHighlighting
      ? this.highlightedNodeIdsWhileHighlighting
      : this.externalHighlightedNodeIds;
  }

  get hasHighlightedNodes() {
    return this.currentlyHighlightedNodeIds.length > 0;
  }

  get hasHighlightedExitNodes() {
    return this.currentlyHighlightedNodeIds.some((actionId) => {
      let actionModel = this.store.peekRecord('action', actionId);
      if (actionModel == null) {
        return false;
      }

      return actionModel.isExit;
    });
  }

  get showHighlightControlPanel() {
    return (
      this.hasHighlightedNodes &&
      !this.args.isCopyingFrom &&
      !this.args.isCopyingTo &&
      !this.isSelectingDropAreaThroughClick &&
      !this.isCopyingHighlightedActions &&
      !this.isMovingHighlightedActions
    );
  }

  get isDisabledDueToRedactedUser() {
    return this.auth.redacted && this.selectedAction?.type === 'webhook_action';
  }

  private updateHighlightedNodesWhileHighlighting() {
    let intersectingNodes = this.findNodesIntersectingSelectionRect();

    if (!isEqual(this.intersectingNodeIds, intersectingNodes)) {
      this.intersectingNodeIds = intersectingNodes;
      this.hasChangedHighlight();
    }
  }

  private findNodesIntersectingSelectionRect() {
    let scale = this.zoomManager.zoomScale;

    let top = this.selectionRectEdges.top / scale;
    let bottom = this.selectionRectEdges.bottom / scale;
    let left = this.selectionRectEdges.left / scale;
    let right = this.selectionRectEdges.right / scale;

    let { nodes } = this;

    let nodeIds: string[] = [];

    // Determine which nodes overlap the selection rectangle
    // https://stackoverflow.com/a/306332/12679717
    for (let i = 0, l = nodes.length; i < l; i++) {
      let n = nodes[i];
      if (
        n.typeCategory === 'action' &&
        n.type !== 'exit_action' && // Don't allow highlighting exits
        n.type !== 'filter_match_delay_action' && // Don't allow highlighting triggers
        top < n.y + n.height / 2 &&
        bottom > n.y - n.height / 2 &&
        left < n.x + n.width / 2 &&
        right > n.x - n.width / 2
      ) {
        nodeIds.push(n.id);
      }
    }

    return nodeIds;
  }

  @computed('selectionRectCoordinates.{startX,startY,currentX,currentY}')
  get selectionRectEdges() {
    let coords = this.selectionRectCoordinates;

    let width = Math.abs(coords.currentX - coords.startX);
    let height = Math.abs(coords.currentY - coords.startY);
    let left = 0;
    let top = 0;

    if (coords.currentX > coords.startX) {
      left = coords.startX;
    } else {
      left = coords.currentX;
    }

    if (coords.currentY > coords.startY) {
      top = coords.startY;
    } else {
      top = coords.currentY;
    }

    return {
      top,
      left,
      right: left + width,
      bottom: top + height,
    };
  }

  @computed('selectionRectEdges.{top,right,bottom,left}', 'isMouseDown', 'isHighlighting')
  get selectionRectStyles() {
    if (!this.isMouseDown || !this.isHighlighting) {
      return htmlSafe(`
--display: none;
`);
    }

    return htmlSafe(`
--display: block;
--width: ${this.selectionRectEdges.right - this.selectionRectEdges.left}px;
--height: ${this.selectionRectEdges.bottom - this.selectionRectEdges.top}px;
--x: ${this.selectionRectEdges.left}px;
--y: ${this.selectionRectEdges.top}px;
`);
  }

  @action
  onContextMenu(event: Event) {
    event.preventDefault();
    // eslint-disable-next-line spellcheck/spell-checker
    document.removeEventListener('contextmenu', this.onContextMenu);
  }

  @action
  onMouseDown(event: MouseEvent) {
    // Reset shift state based on other events, because sometimes a
    // keyDown/keyUp may not be caught
    if (this.isShiftHeld && !event.shiftKey) {
      this.isShiftHeld = false;
    }

    if (
      (event.button !== 0 && event.button !== 2) ||
      // If no canvas element registered
      !this.canvas ||
      // If we're in the highlight mode (default), but we're performing a copy
      // or move (through clicking, for example)
      (this.mouseMode === MouseMode.Highlight && (this.isCopyingHighlightedActions || this.isMovingHighlightedActions))
    ) {
      return;
    }

    // If right mouse button, enter scroll mode
    if (event.button === 2) {
      // eslint-disable-next-line spellcheck/spell-checker
      document.addEventListener('contextmenu', this.onContextMenu);
      this.mouseMode = MouseMode.Scroll;
    }

    this.isMouseDown = true;
    this.hasMovedMouse = false;

    let graphRect = this.canvas.querySelector('.vwb-v2-graph')?.getBoundingClientRect();

    // This scaling is needed if the VWB is within a container with transform:
    // scale applied. Otherwise the coordinates will be off.
    let containerScale = this.canvas.offsetWidth / this.canvas.getBoundingClientRect().width;

    let x = (event.clientX - graphRect!.x) * containerScale;
    let y = (event.clientY - graphRect!.y) * containerScale;
    this.selectionRectCoordinates = {
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
    };

    this.mousePosition = {
      x: event.clientX * containerScale,
      y: event.clientY * containerScale,
    };
    this.startDragScrollCoordinates = {
      x: event.clientX * containerScale,
      y: event.clientY * containerScale,
    };
    this.startScrollPosition = {
      x: this.canvas.scrollLeft,
      y: this.canvas.scrollTop,
    };

    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('mousemove', this.onMouseMove);

    document.body.classList.add('u-user-select-none');

    if (this.isHighlighting && this.needsHighlightAutoscrollAnimationFrame) {
      this.needsHighlightAutoscrollAnimationFrame = false;
      this.highlightAutoScrollAnimationFrame = requestAnimationFrame(this.autoScrollCanvasWhenHighlightingCloseToEdges);
    }

    this.canvas?.dispatchEvent(
      new CustomEvent('vwb:canvas:mousedown', {
        detail: {
          mouseMode: this.mouseMode,
          dragMode: this.dragMode,
          hasMovedMouse: this.hasMovedMouse,
          originalEvent: event,
        },
      }),
    );
  }

  @tracked hasMovedMouse = false;
  mousePosition: { x: number; y: number } | null = null;

  mouseMoveWaiterToken: any = null;

  @action
  onMouseMove(event: MouseEvent) {
    if (!this.isMouseDown || event.defaultPrevented || !this.canvas) {
      return;
    }

    this.mousePosition = { x: event.clientX, y: event.clientY };

    if (this.needsMouseMoveAnimationFrame) {
      this.needsMouseMoveAnimationFrame = false;
      if (this.mouseMoveWaiterToken != null) {
        mouseMoveWaiter.endAsync(this.mouseMoveWaiterToken);
      }
      this.mouseMoveWaiterToken = mouseMoveWaiter.beginAsync();
      this.mouseMoveAnimationFrame = requestAnimationFrame(this.performOnMouseMove);
    }

    this.canvas?.dispatchEvent(
      new CustomEvent('vwb:canvas:mousemove', {
        detail: {
          mouseMode: this.mouseMode,
          dragMode: this.dragMode,
          hasMovedMouse: this.hasMovedMouse,
          originalEvent: event,
        },
      }),
    );
  }

  @action
  performOnMouseMove() {
    if (!this.canvas || !this.mousePosition) {
      if (this.mouseMoveWaiterToken != null) {
        mouseMoveWaiter.endAsync(this.mouseMoveWaiterToken);
        this.mouseMoveWaiterToken = null;
      }
      return;
    }

    this.needsMouseMoveAnimationFrame = true;
    if (this.mouseMoveAnimationFrame !== 0) {
      cancelAnimationFrame(this.mouseMoveAnimationFrame);
      this.mouseMoveAnimationFrame = 0;
    }
    let diffCoordinates = {
      x: this.mousePosition.x - this.startDragScrollCoordinates.x,
      y: this.mousePosition.y - this.startDragScrollCoordinates.y,
    };

    if (!this.hasMovedMouse && (diffCoordinates.x !== 0 || diffCoordinates.y !== 0)) {
      this.hasMovedMouse = true;

      // If we're beginning a new highlight and not holding shift, reset the
      // currently highlighted nodes
      if (this.isHighlighting && !this.isShiftHeld) {
        this.args.updateHighlightedNodeIds([]);
        this.hasChangedHighlight();
      }
    }

    if (this.hasMovedMouse) {
      if (this.isHighlighting) {
        this.updateSelectionRectCoordinatesAndNodes.perform();

        if (this.needsHighlightAutoscrollAnimationFrame) {
          this.needsHighlightAutoscrollAnimationFrame = false;
          this.highlightAutoScrollAnimationFrame = requestAnimationFrame(
            this.autoScrollCanvasWhenHighlightingCloseToEdges,
          );
        }
      } else {
        this.canvas.scrollLeft = this.startScrollPosition.x - diffCoordinates.x;
        this.canvas.scrollTop = this.startScrollPosition.y - diffCoordinates.y;
      }
    }

    if (this.mouseMoveWaiterToken != null) {
      mouseMoveWaiter.endAsync(this.mouseMoveWaiterToken);
      this.mouseMoveWaiterToken = null;
    }
  }

  updateSelectionRectCoordinatesAndNodes = task({ keepLatest: true }, async () => {
    if (!this.canvas || !this.mousePosition) {
      return;
    }

    let previousX = this.selectionRectCoordinates.currentX;
    let previousY = this.selectionRectCoordinates.currentY;

    let graphRect = this.canvas.querySelector('.vwb-v2-graph')?.getBoundingClientRect();

    // This scaling is needed if the VWB is within a container with transform:
    // scale applied. Otherwise the coordinates will be off.
    let containerScale = this.canvas.offsetWidth / this.canvas.getBoundingClientRect().width;

    let x = (this.mousePosition.x - graphRect!.x) * containerScale;
    let y = (this.mousePosition.y - graphRect!.y) * containerScale;
    this.selectionRectCoordinates = {
      startX: this.selectionRectCoordinates.startX,
      startY: this.selectionRectCoordinates.startY,
      currentX: x,
      currentY: y,
    };

    if (x !== previousX || y !== previousY) {
      this.updateHighlightedNodesWhileHighlighting();
    }
  });

  // Keep track of whether animation frames have called their callbacks yet. If
  // these are false, we don't need to schedule any fresh animation frames yet
  needsHighlightAutoscrollAnimationFrame = true;
  needsMouseMoveAnimationFrame = true;
  highlightAutoScrollAnimationFrame = 0;
  mouseMoveAnimationFrame = 0;

  @action
  private autoScrollCanvasWhenHighlightingCloseToEdges() {
    if (!this.isHighlighting || !this.mousePosition || !this.canvas) {
      return;
    }

    this.needsHighlightAutoscrollAnimationFrame = true;
    if (!this.hasMovedMouse) {
      return;
    }

    if (this.highlightAutoScrollAnimationFrame !== 0) {
      cancelAnimationFrame(this.highlightAutoScrollAnimationFrame);
      this.highlightAutoScrollAnimationFrame = 0;
    }

    let { canvas } = this;

    let canvasRect = canvas.getBoundingClientRect();

    let edgeDistances = {
      top: this.mousePosition.y - canvasRect.top,
      bottom: canvasRect.bottom - this.mousePosition.y,
      right: canvasRect.right - this.mousePosition.x,
      left: this.mousePosition.x - canvasRect.left,
    };

    if (edgeDistances.top < AUTOSCROLL_THRESHOLD) {
      let percentageOfThreshold = (AUTOSCROLL_THRESHOLD - edgeDistances.top) / AUTOSCROLL_THRESHOLD;
      canvas.scrollTop -= AUTOSCROLL_SPEED * percentageOfThreshold;
    } else if (edgeDistances.bottom < AUTOSCROLL_THRESHOLD) {
      let percentageOfThreshold = (AUTOSCROLL_THRESHOLD - edgeDistances.bottom) / AUTOSCROLL_THRESHOLD;
      canvas.scrollTop += AUTOSCROLL_SPEED * percentageOfThreshold;
    }

    if (edgeDistances.left < AUTOSCROLL_THRESHOLD) {
      let percentageOfThreshold = (AUTOSCROLL_THRESHOLD - edgeDistances.left) / AUTOSCROLL_THRESHOLD;
      canvas.scrollLeft -= AUTOSCROLL_SPEED * percentageOfThreshold;
    } else if (edgeDistances.right < AUTOSCROLL_THRESHOLD) {
      let percentageOfThreshold = (AUTOSCROLL_THRESHOLD - edgeDistances.right) / AUTOSCROLL_THRESHOLD;
      canvas.scrollLeft += AUTOSCROLL_SPEED * percentageOfThreshold;
    }

    requestAnimationFrame(this.autoScrollCanvasWhenHighlightingCloseToEdges);
    this.updateSelectionRectCoordinatesAndNodes.perform();
  }

  @action
  onMouseUp(event: MouseEvent) {
    // Reset shift state based on other events, because sometimes a
    // keyDown/keyUp may not be caught
    this.isShiftHeld = event.shiftKey;

    if (this.mouseMode === MouseMode.Highlight && (this.args.isCopyingFrom ? true : !this.args.isReadOnly)) {
      this.args.updateHighlightedNodeIds(this.highlightedNodeIdsWhileHighlighting);
      this.intersectingNodeIds = [];
      this.hasChangedHighlight();
    }

    this.intersectingNodeIds = [];

    if (!this.hasMovedMouse && !this.args.isCopyingTo && (this.args.isCopyingFrom ? true : !this.args.isReadOnly)) {
      // If the mouse hasn't been moved, treat this as a click, which dismisses
      // the shadows on new actions
      this.justAddedActionIds = [];

      if (!this.isShiftHeld) {
        this.args.updateHighlightedNodeIds([]);
        this.hasChangedHighlight();
      }
    }

    this.canvas?.dispatchEvent(
      new CustomEvent('vwb:canvas:mouseup', {
        detail: {
          mouseMode: this.mouseMode,
          dragMode: this.dragMode,
          hasMovedMouse: this.hasMovedMouse,
          originalEvent: event,
        },
      }),
    );

    if (event.button === 2) {
      this.mouseMode = MouseMode.Highlight;
    }

    this.isMouseDown = false;
    this.hasMovedMouse = false;

    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    document.body.classList.remove('u-user-select-none');

    if (!event.altKey) {
      this.changeDragMode(DragMode.Move);
    }

    this.mousePosition = null;

    this.needsMouseMoveAnimationFrame = true;
    this.needsHighlightAutoscrollAnimationFrame = true;
  }

  @tracked dragMode: DragMode = DragMode.Move;

  @action
  changeDragMode(dragMode: DragMode) {
    if (this.dragMode !== dragMode) {
      this.dragMode = dragMode;
    }

    let copyCursorClass = this.demoMode ? 'vwb-v2-demo-copy-cursor' : 'vwb-v2-copy-cursor';

    if (dragMode === DragMode.Copy) {
      document.body.classList.add(copyCursorClass);
    }

    if (dragMode === DragMode.Move) {
      document.body.classList.remove(copyCursorClass);
    }
  }

  @tracked canPerformCopy = true;
  @tracked canPerformMove = true;
  @tracked canPerformDelete = true;

  #checkRedactedAuthorPerms() {
    // if the highlighted node is a webhook and user is redacted author, disable copy and delete
    if (this.isDisabledDueToRedactedUser) {
      this.canPerformCopy = false;
      this.canPerformDelete = false;
    }
  }

  @action
  hasChangedHighlight(action?: ActionModel) {
    if (action) {
      this.selectedAction = action;
    }

    if (this.currentlyHighlightedNodeIds.length === 0) {
      this.canPerformCopy = true;
      this.canPerformMove = true;
      this.canPerformDelete = true;

      if (action) {
        this.#checkRedactedAuthorPerms();
      }

      if (this.args.onCanPerformHighlightOperationChanged) {
        this.args.onCanPerformHighlightOperationChanged({
          canCopy: this.canPerformCopy,
          canMove: this.canPerformMove,
          canDelete: this.canPerformDelete,
        });
      }

      return;
    }
    let newEdges = connectNodesIntoSubGraph(
      this.args.campaign.consolidatedTree.edges,
      this.currentlyHighlightedNodeIds,
    );

    let hasOutEdge = newEdges.some((e) => e.to === 'OUT');
    // If everything is fine, getNodeIdsWithoutIncomingEdges will return an
    // empty array, since the "root" node of the reduced graph has an incoming
    // edge from "IN". If we get any nodes that don't have incoming edges, that
    // means the graph produced by connectNodesIntoSubGraph doesn't form one
    // chain and as such isn't moveable or copyable (as we wouldn't know how to
    // arrange the results)
    // At the moment, the only scenario (I believe) when that happens is when
    // selecting a branch action whose branches all end in exits.
    // This could happen with other selections too (for example, when multiple
    // exits are selected), but we don't allow selecting just exits, and
    // selecting a branch action selects all of its internals too.
    let hasMoreThanOneRootNode = getNodeIdsWithoutIncomingEdges(newEdges, this.currentlyHighlightedNodeIds).length > 0;

    this.canPerformCopy = hasOutEdge && !hasMoreThanOneRootNode;
    this.canPerformMove = this.canPerformCopy;
    this.canPerformDelete = this.canPerformCopy;

    if (this.doesHighlightContainCollectionActions) {
      this.canPerformCopy = false;
    }

    if (action) {
      this.#checkRedactedAuthorPerms();
    }

    if (this.args.onCanPerformHighlightOperationChanged) {
      this.args.onCanPerformHighlightOperationChanged({
        canCopy: this.canPerformCopy,
        canMove: this.canPerformMove,
        canDelete: this.canPerformDelete,
      });
    }
  }

  @cached
  get doesHighlightContainCollectionActions(): boolean {
    if (!this.account.planFeatures.collections.enabled) {
      return this.currentlyHighlightedNodeIds.some((id: any) => {
        let node = this.currentGraph?.nodes[id];
        if (node == null || node.typeCategory !== 'action') {
          return false;
        }
        return node.action.isCollectionAction;
      });
    }
    return false;
  }

  // eslint-disable-next-line require-yield
  *sidebarTransition({ insertedSprites, removedSprites }: { insertedSprites: Sprite[]; removedSprites: Sprite[] }) {
    for (let sprite of removedSprites) {
      sprite.endAtPixel({ x: -SIDEBAR_TRANSLATE_OFFSET });
      move(sprite, { easing: easeIn });
    }

    for (let sprite of insertedSprites) {
      sprite.startAtPixel({ x: -SIDEBAR_TRANSLATE_OFFSET });
      move(sprite, { easing: easeOut });
    }
  }

  transitionDuration = config.APP.workflowAnimationDuration;
  // eslint-disable-next-line require-yield
  *nodeTransition({
    insertedSprites,
    keptSprites,
    removedSprites,
  }: {
    insertedSprites: Sprite[];
    keptSprites: Sprite[];
    removedSprites: Sprite[];
  }) {
    for (let sprite of removedSprites) {
      let { nodeInViewport } = (sprite.element as HTMLElement).dataset;
      if (nodeInViewport === 'true') {
        let { nodeType } = (sprite.element as HTMLElement).dataset;
        if (nodeType !== 'virtual_drop_area' && nodeType !== 'drop_area_icon') {
          fadeOut(sprite, { easing: easeIn });
        } else {
          sprite.hide();
        }
      }
    }

    for (let sprite of keptSprites) {
      let { nodeInViewport } = (sprite.element as HTMLElement).dataset;
      if (nodeInViewport === 'true') {
        // Don't animate regular nodes - those animations are handled by CSS
        // transitions
        let { nodeType } = (sprite.element as HTMLElement).dataset;
        if (nodeType === 'virtual_drop_area' || nodeType === 'drop_area_icon') {
          sprite.hide();
        }
      }
    }

    for (let sprite of insertedSprites) {
      let { nodeType } = (sprite.element as HTMLElement).dataset;
      if (nodeType !== 'virtual_drop_area' && nodeType !== 'drop_area_icon') {
        fadeIn(sprite, { easing: easeOut });
      }
    }
  }

  *pathTransition({
    insertedSprites,
    keptSprites,
    removedSprites,
  }: {
    insertedSprites: Sprite[];
    keptSprites: Sprite[];
    removedSprites: Sprite[];
  }) {
    removedSprites.forEach((sprite) => fadeOut(sprite, { easing: easeIn }));
    insertedSprites.forEach((sprite) => fadeIn(sprite, { easing: easeOut }));

    let newPaths: Record<string, string> = {};
    yield Promise.all(
      keptSprites.map((sprite) => {
        let spriteId = (sprite.element as HTMLElement).dataset.id as string;
        let oldPath = this.oldPathsMap[spriteId];
        let newPath = this.pathsMap[spriteId];
        if (oldPath && newPath) {
          if (
            Math.abs(oldPath.segment[0].x - newPath.segment[0].x) < 3 &&
            Math.abs(oldPath.segment[0].y - newPath.segment[0].y) < 3 &&
            oldPath.segment[1].command === newPath.segment[1].command &&
            Math.abs(oldPath.segment[1].x - newPath.segment[1].x) < 3 &&
            Math.abs(oldPath.segment[1].y - newPath.segment[1].y) < 3
          ) {
            return null;
          }
          let currentPathD = sprite.element.getAttribute('d') as string;
          newPaths[spriteId] = currentPathD;
          // A path's "d" property changes immediately, so we need to set the old
          // one before we animate them.
          sprite.element.setAttribute('d', oldPath.d);
          return fadeOut(sprite, {
            duration: this.transitionDuration / 2,
            easing: easeIn,
          });
        }

        return null;
      }),
    );
    yield keptSprites.map((sprite) => {
      let spriteId = (sprite.element as HTMLElement).dataset.id as string;
      let newPath = newPaths[spriteId];
      if (newPath) {
        sprite.element.setAttribute('d', newPath);
        return fadeIn(sprite, {
          duration: this.transitionDuration / 2,
          easing: easeOut,
        });
      }

      return null;
    });
  }

  @notEmpty('args.selectedActionAbstract') isEditing!: boolean;
  @or(
    'args.addActionTask.isRunning',
    'copyHighlightedActions.isRunning',
    'deleteHighlightedActions.isRunning',
    'moveHighlightedActions.isRunning',
    'addOrMoveActionTask.isRunning',
    'duplicateActionTask.isRunning',
    'rollbackLastOperation.isRunning',
  )
  isSaving!: boolean;

  @tracked futureSelectedAction: ActionModel | null = null;

  @action
  onSelect(actionModel: ActionModel, event: MouseEvent | null) {
    if (event) {
      // Reset shift state based on other events, because sometimes a
      // keyDown/keyUp may not be caught
      if (this.isShiftHeld && !event.shiftKey) {
        this.isShiftHeld = false;
      }
    }

    // Ignore "onSelect" if shift is being held and there is no action abstract.
    // This means we're wanting to select/deselect action items to copy/bulk
    // delete
    if (this.isShiftHeld && !this.args.selectedActionAbstract) {
      return;
    }

    // If we turn an action into a split test, the actionAbstract will remain
    // of type "message", but the underlying action it's linked to will change
    // into a different type (rendering the actionAbstract useless, because
    // split is its own actionAbstract).
    // Changing type into split may trigger the abstract to become dirty, due
    // to changes in the action's properties. If such a type change occurred,
    // ignore dirtyness and just change to the new action abstract.

    let actionChangedType = this.args.selectedActionAbstract
      ? this.args.selectedActionAbstract.actionType !== this.args.selectedActionAbstract.action.type
      : false;

    if (this.args.selectedActionAbstract?.isDirty && !actionChangedType) {
      this.futureSelectedAction = actionModel;
    } else {
      let actionAbstract = createActionAbstract(actionModel);

      if (
        isNone(this.args.selectedActionAbstract) ||
        this.args.selectedActionAbstract?.action.id !== actionModel.id ||
        this.args.selectedActionAbstract?.type !== actionAbstract.type
      ) {
        this.futureSelectedAction = null;

        // previously: this.set("selectedActionAbstract", actionAbstract)
        // need to use closure action instead of overwriting component param passed from controller to avoid bug causing multiple animated elements being left in DOM when using "animated-value" ember-animated component: introduced with Ember 3.11, fixed in Ember 3.13-beta.3
        // more details: https://github.com/customerio/ui/pull/3274
        // TODO: fully refactor or revert to previous implementation after Ember 3.13 upgrade
        this.args.onSelectedAction(actionAbstract);

        // Clicking an action stops propagating the click, so the mouseup that
        // resets the shadows won't reach the VWB - reset the shadows.
        this.justAddedActionIds = [];
      }
    }
  }

  @action
  onDeselect() {
    let { futureSelectedAction } = this;
    let actionAbstract = isNone(futureSelectedAction) ? null : createActionAbstract(futureSelectedAction);

    this.futureSelectedAction = null;
    this.args.onSelectedAction(actionAbstract);

    // Reset shadows
    this.justAddedActionIds = [];
  }

  @tracked grayedOutActionIds: string[] = [];

  @action
  onActionDragStart(actionModel: ActionModel) {
    // We don't need to gray out actions when the drag is a copy operation
    if (this.dragMode === DragMode.Copy) {
      this.analytics.track('campaign', 'workflow_existing_item_copy_started', {
        model: this.args.campaign,
        actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
        trayTab: this.args.tab,
        initiationType: 'keyboard',
      });
      return;
    }

    // Bulk move
    if (this.externalHighlightedNodeIds.length > 0 && this.externalHighlightedNodeIds.includes(actionModel.id)) {
      let highlightedActions = this.externalHighlightedNodeIds.map((id) => this.store.peekRecord('action', id));
      let branchActions = highlightedActions.filter((a) => a?.isBranch) as ActionModel[];
      this.grayedOutActionIds = branchActions.reduce((acc, actionModel) => {
        return [...acc, ...actionModel.subActions];
      }, [] as string[]);

      this.analytics.track('campaign', 'workflow_existing_item_move_started', {
        model: this.args.campaign,
        actions: highlightedActions,
        trayTab: this.args.tab,
        initiationType: 'keyboard',
      });
      return;
    }

    this.analytics.track('campaign', 'workflow_existing_item_move_started', {
      model: this.args.campaign,
      actions: [actionModel],
      trayTab: this.args.tab,
      initiationType: 'keyboard',
    });

    // We want to gray out actions only when a branch is being dragged
    if (!actionModel?.isBranch) {
      return;
    }

    this.grayedOutActionIds = actionModel.subActions;
  }

  @action
  onActionDragEnd() {
    next(this, () => {
      if (this.isDestroyed) {
        return;
      }

      this.grayedOutActionIds = [];
    });
  }

  @tracked copyFromCampaign: CampaignModel | null = null;
  @tracked isMovingHighlightedActions = false;
  @tracked isCopyingHighlightedActions = false;

  @action
  startHighlightMoveThroughClick() {
    if (!this.canPerformMove) {
      return;
    }

    this.analytics.track('campaign', 'workflow_existing_item_move_started', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
      trayTab: this.args.tab,
      initiationType: 'action menu',
    });
    this.isMovingHighlightedActions = true;
    this.isSelectingDropAreaThroughClick = true;
  }

  @action
  startHighlightCopyThroughClick() {
    if (!this.canPerformCopy) {
      return;
    }

    this.analytics.track('campaign', 'workflow_existing_item_copy_started', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
      trayTab: this.args.tab,
      initiationType: 'action menu',
    });

    this.copyFromCampaign = this.args.campaign;
    this.isCopyingHighlightedActions = true;
    this.isSelectingDropAreaThroughClick = true;
  }

  @action
  startHighlightCopyTo() {
    if (!this.canPerformCopy) {
      return;
    }

    this.analytics.track('campaign', 'workflow_existing_item_copy_to_started', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
    });

    this.isShowingCopyToModal = true;
  }

  @tracked isShowingBulkDeleteWarningModal = false;

  highlightDeleteAnalyticsInitiationType = '';

  @action
  startHighlightDelete(initiationType = 'action menu') {
    if (!this.canPerformDelete) {
      return;
    }

    this.highlightDeleteAnalyticsInitiationType = initiationType;
    this.analytics.track('campaign', 'workflow_existing_item_delete_started', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
      trayTab: this.args.tab,
      initiationType: this.highlightDeleteAnalyticsInitiationType,
    });
    this.isShowingBulkDeleteWarningModal = true;
  }

  @action
  dismissHighlightPanel() {
    this.resetNodeTranslations();

    if (this.isMovingHighlightedActions || this.isCopyingHighlightedActions) {
      this.isMovingHighlightedActions = false;
      this.isCopyingHighlightedActions = false;

      if (this.externalHighlightedNodeIds.some((id) => id.includes('copy'))) {
        this.args.updateHighlightedNodeIds([]);
      }
    } else {
      this.args.updateHighlightedNodeIds([]);
    }
  }

  deleteHighlightedActions = task(async (waitUntilDeleteOption: 'continue' | 'exitEarly' | '' = '') => {
    if (!this.args.highlightedNodeIds || this.args.highlightedNodeIds.length === 0) {
      return;
    }

    let result = bulkDelete(this.args.highlightedNodeIds, this.args.campaign, this.currentGraph!);

    if (!result) {
      return;
    }

    let ids = [...this.args.highlightedNodeIds];
    // Check if we are deleting multi-language branch action(s).
    for (let actionId of [...ids]) {
      ids = [
        ...ids,
        ...getActionIdsInConsolidatedGroup(
          actionId,
          this.args.campaign.consolidatedActionsGroups,
          this.args.campaign.parentConsolidatedBranchMap,
        ),
      ];
    }

    this.notifyAddMoveOrDelete();

    await this.args.campaign.deleteActions({
      actionIds: ids.uniq(),
      edges: result.newEdges,
      touchedActionIds: [],
      waitUntilDeleteOption,
    });

    this.analytics.track('campaign', 'workflow_existing_item_delete_confirmed', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
      trayTab: this.args.tab,
      initiationType: this.highlightDeleteAnalyticsInitiationType,
    });

    this.args.campaign.unloadActions(ids);
    this.args.updateHighlightedNodeIds([]);
    this.isShowingBulkDeleteWarningModal = false;

    // If the delete was initiated from the edit sidebar, deselect the deleted
    // action
    if (this.args.selectedActionAbstract) {
      this.onDeselect();
    }

    this.args.campaign.parseTree();
    this.justAddedActionIds = [];
    this.previousEdges = null;
    this.previousActionIds = null;
  });

  copyHighlightedActions = task(
    { restartable: true },
    async (
      sourceCampaign: CampaignModel,
      targetCampaign: CampaignModel,
      sourceEnvironmentId: string,
      targetEnvironmentId: string,
      highlightedNodeIds: string[],
      dropTargetItem: DropTargetItem,
      dropType: DropType,
      copyTo?: boolean,
      analyticsCallback?: () => unknown,
    ) => {
      this.isSelectingDropAreaThroughClick = false;
      this.isCopyingHighlightedActions = false;
      this.isPerformingDragOperation = false;

      let actionModels = highlightedNodeIds.map((id) => this.store.peekRecord('action', id)!);
      let childActionModels = actionModels.reduce((childActions, actionModel) => {
        if (!actionModel.isSplitTest) {
          return childActions;
        }

        let parentBranchId = sourceCampaign.parentConsolidatedBranchMap[actionModel.id];
        if (parentBranchId != null) {
          childActions = [
            ...childActions,
            this.store.peekRecord('action', parentBranchId)!,
            ...sourceCampaign.getConsolidatedBranchChildActions(parentBranchId)!,
          ];
        } else {
          childActions = [...childActions, ...actionModel.actions.toArray()];
        }

        return childActions;
      }, [] as ActionModel[]);

      let allActionModels = [...actionModels, ...childActionModels];

      // We only add warnings in the "copy to" flow here, because in the "copy
      // from" flow it happens from within the "copy from" modal beforehand
      if (copyTo) {
        // Currently it's not possible to copy only a variation, so the
        // "addConditionsToChildActions" function doesn't really do anything.
        // Still, we're invoking it just in case in future we decide to add that
        // feature.
        addConditionsToChildActions(actionModels, sourceCampaign.actions.toArray());

        addWarningsToActions(
          allActionModels,
          sourceCampaign,
          targetCampaign,
          this.store.peekRecord('environment', sourceEnvironmentId)!,
          this.store.peekRecord('environment', targetEnvironmentId)!,
          true,
        );
        updateActionsBasedOnWarnings(allActionModels);
      }

      let insertAtEdges = prepareInsertAtEdges(dropTargetItem, dropType, targetCampaign);

      let { newEdges, originalIdMap, serializedActions } = prepareExpandedEdgesForBulkCopy(
        sourceCampaign,
        targetCampaign,
        highlightedNodeIds,
        insertAtEdges,
        this.store,
      );

      let newSerializedActions: any[] = [];
      serializedActions.forEach((a: any) => {
        let action = { ...a };
        action._copy = true;
        if (a.type !== 'split_randomized_action') {
          newSerializedActions.push(action);
          return;
        }

        let actionModel = this.store.peekRecord('action', originalIdMap[action.id]);
        let children =
          actionModel?.actions
            .toArray()
            .map((a) => a.serialize({ includeId: true }))
            .map((a: any, index: number) => ({
              ...a,
              id: `${action.id}_${index}`,
              _copy: true,
              parent_action_id: action.id,
            })) ?? [];

        action.action_ids = children.map((a) => a.id);
        newSerializedActions.push(action);
        newSerializedActions.push(...children);
      });

      newSerializedActions.forEach((a: any) => {
        if (a.campaign_id) {
          a.campaign_id = a.campaign_id.replace('copy_', '');
        }
        if (a.template_id) {
          a.template_id = a.template_id.replace('copy_', '');
        }
        if (a.parent_action_id) {
          a.parent_action_id = a.parent_action_id.replace('copy_', '');
        }
      });

      if (copyTo) {
        this.previousEdges = [...targetCampaign.edges];
        this.previousActionIds = [...targetCampaign.workflowActionIds];

        this.store.pushPayload({
          actions: newSerializedActions.map((a: any) => {
            let aCopy = { ...a };
            if (aCopy.campaign_id) {
              aCopy.campaign_id = targetCampaign.id;
            }
            if (aCopy.template_id) {
              aCopy.template_id = `copy_${aCopy.template_id}`;
            }

            return aCopy;
          }),
        });

        let newWorkflowActionIds = [
          ...targetCampaign.workflowActionIds,
          ...newSerializedActions.filter((a) => a.parent_action_id == null).map((a) => a.id),
        ];

        targetCampaign.setProperties({
          edges: newEdges,
          workflowActionIds: newWorkflowActionIds,
        });

        this.justAddedActionIds = [...targetCampaign.workflowActionIds].filter(
          (id) => !this.previousActionIds.includes(id),
        );

        // If performing a "copy to" we're actually accidentally modifying the
        // current campaign's actions when adding warnings. This rolls back the
        // changes.
        allActionModels.forEach((action) => action.rollbackAttributes());
        targetCampaign.parseTree(true);

        if (this.args.onCopyToFinished) {
          this.args.onCopyToFinished(targetCampaign.serialize({ includeId: true }), newSerializedActions);
        }
      } else {
        // The backend expects variation actions to have their ids set to '0', but
        // we temporarily set them to other IDs to avoid ID conflicts in the Ember
        // Data store. Here, we reset them again.
        newSerializedActions = newSerializedActions.map((a: any) => {
          if (a.parent_action_id == null) {
            return a;
          }

          a.id = '0';
          return a;
        });

        let data = {
          edges: newEdges,
          actions: newSerializedActions,
          copyFromEnv: sourceEnvironmentId,
        };

        this.previousEdges = [...targetCampaign.edges];
        this.previousActionIds = [...targetCampaign.workflowActionIds];

        await targetCampaign.addActions(data);

        if (analyticsCallback) {
          analyticsCallback();
        }

        targetCampaign.parseTree();
        this.copyFromEnvironmentId = null;
        this.copyFromCampaign = null;

        this.onDeselect();
        this.justAddedActionIds = [...targetCampaign.workflowActionIds].filter(
          (id) => !this.previousActionIds.includes(id),
        );

        // Highlight the newly inserted actions
        this.args.updateHighlightedNodeIds(this.justAddedActionIds);
      }
    },
  );

  moveHighlightedActions = task({ drop: true }, async (dropItem: DropTargetItem, dropType: DropType) => {
    let wasSelectingThroughClick = this.isSelectingDropAreaThroughClick;

    this.isSelectingDropAreaThroughClick = false;
    this.isMovingHighlightedActions = false;
    this.isPerformingDragOperation = false;

    let originalCampaignEdges = [...this.args.campaign.consolidatedTree.edges];
    let previousEdges = [...this.args.campaign.edges];
    let insertAtEdges = prepareInsertAtEdges(dropItem, dropType, this.args.campaign);

    let deleteResult = bulkDelete(
      this.externalHighlightedNodeIds,
      this.args.campaign,
      this.currentGraph!,
      insertAtEdges,
    );

    if (!deleteResult) {
      return;
    }

    let edgesAfterDelete = deleteResult.newEdges;
    let insertAtEdgesAfterDelete = deleteResult.edgesToUpdate;

    this.args.campaign.set('branchRejoinPointsMap', deleteResult.rejoinPointsMap);

    let allIdsByTraversalOrder = this.args.campaign.traversedActions.map((a) => a.id);
    // We sort edge original edges according to traversal order, so that the
    // outcome of the move also flows according to the original order
    let sortedSourceEdges = [...originalCampaignEdges].sort((a, b) => {
      return allIdsByTraversalOrder.indexOf(a.to) - allIdsByTraversalOrder.indexOf(b.to);
    });

    let bulkCopyResult = prepareEdgesForBulkCopy(
      sortedSourceEdges,
      edgesAfterDelete,
      this.externalHighlightedNodeIds,
      insertAtEdgesAfterDelete,
      this.store,
    );

    await this.args.campaign.moveAction({
      edges: bulkCopyResult.newEdges,
      touchedActionIds: [],
    });

    this.analytics.track('campaign', 'workflow_existing_item_move_completed', {
      model: this.args.campaign,
      actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
      trayTab: this.args.tab,
      initiationType: wasSelectingThroughClick ? 'action menu' : 'keyboard',
    });

    this.args.campaign.parseTree();
    this.previousEdges = previousEdges;
    this.previousActionIds = null;
  });

  copyToModalPerformCopy = task(
    { restartable: true },
    async (serializedTargetCampaign: any, serializedActions: any[], targetEnvironmentId: string) => {
      let campaign = { ...serializedTargetCampaign };
      let targetCampaignId = serializedTargetCampaign.id.replace('copy_', '');

      let isCurrentCampaign = this.args.campaign.id === targetCampaignId;

      if (isCurrentCampaign) {
        this.previousEdges = [...this.args.campaign.edges];
        this.previousActionIds = [...this.args.campaign.workflowActionIds];
      }

      campaign.id = targetCampaignId;
      campaign.edges = campaign.edges.map((edge: Edge) => ({
        ...edge,
        from: edge.from.replace('copy_', ''),
        to: edge.to.replace('copy_', ''),
      }));
      campaign.update_type = CampaignUpdateTypes.AddActions;
      campaign.workflow_action_ids = (campaign.workflow_action_ids ?? []).map((id: string) => id.replace('copy_', ''));

      // The backend expects variation actions to have their ids set to '0', but
      // we temporarily set them to other IDs to avoid ID conflicts in the Ember
      // Data store. Here, we reset them again.
      let serializedActionsWithUpdatedVariations = serializedActions.map((a: any) => {
        if (a.parent_action_id == null) {
          return a;
        }

        a.id = '0';
        return a;
      });

      let addActionsData: Record<string, unknown> = {
        actions: serializedActionsWithUpdatedVariations,
        campaign,
        copy_from_env: this.environment.current.id,
      };

      // We need to call this manually because the campaign we're copying to may
      // be in another environment, so it may not be loaded into the store (so we
      // can't call the addActions method on the campaign model)
      await this.api.put(`campaigns/${targetCampaignId}`, {
        replaceEnvironmentID: targetEnvironmentId,
        data: addActionsData,
      });

      this.analytics.track('campaign', 'workflow_existing_item_copy_to_completed', {
        model: this.store.peekRecord('campaign', serializedTargetCampaign.id) as CampaignModel,
        actions: this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id)),
        trayTab: this.args.tab,
        sameEnvironment: targetEnvironmentId === this.environment.current.id ?? '',
      });

      if (isCurrentCampaign) {
        await this.args.campaign.reload();

        this.justAddedActionIds = this.args.campaign.workflowActionIds.filter(
          (id) => !this.previousActionIds.includes(id),
        );
        this.args.campaign.parseTree();
        // Highlight the newly inserted actions
        this.args.updateHighlightedNodeIds(this.justAddedActionIds);
      }
    },
  );

  duplicateActionTask = task({ restartable: true }, async (actionModel: ActionModel) => {
    let dropType: DropType = 'insertAfter';
    let dropItem: DropTargetItem = {
      id: actionModel.id,
      action: actionModel,
      type: 'action',
    };

    let actionIdsToCopy = [actionModel.id];

    if (actionModel.isBranch) {
      dropType = 'insertAtBranchEnd';
      dropItem = this.currentGraph?.nodes[actionModel.id].branch as DropTargetItem;

      actionIdsToCopy.push(...actionModel.subActions);
    }

    this.analytics.track('campaign', 'workflow_existing_item_copy_started', {
      model: this.args.campaign,
      actions: [actionModel],
      trayTab: this.args.tab,
      initiationType: 'edit sidebar',
    });

    await this.copyHighlightedActions.perform(
      this.args.campaign,
      this.args.campaign,
      this.environment.current.id ?? '',
      this.environment.current.id ?? '',
      actionIdsToCopy,
      dropItem,
      dropType,
    );

    this.analytics.track('campaign', 'workflow_existing_item_copy_completed', {
      model: this.args.campaign,
      actions: [actionModel],
      trayTab: this.args.tab,
      initiationType: 'edit sidebar',
    });
  });

  @tracked previousEdges: any = null;
  get hasPreviousEdges() {
    return this.previousEdges != null;
  }

  @tracked previousActionIds: any = null;
  @tracked justAddedActionIds: string[] = [];

  rollbackLastOperation = task({ restartable: true }, async () => {
    if (!this.args.isCopyingTo) {
      // For now, we've decided to not allow any undos in the VWB, see discussion at
      // https://customerio.slack.com/archives/CBTADU1AQ/p1611278144076000
      // However, we still want to have an undo when we're in the "copy to"
      // modal, so that the customer can change their mind about where to place
      // actions.
      // Undos in the "copy to" modal are okay, because the changes aren't
      // persisted until the save button is actually pressed
      return;
    }

    this.args.campaign.setProperties({
      edges: [...this.previousEdges],
      workflowActionIds: [...this.previousActionIds],
    });
    this.args.campaign.parseTree();

    this.justAddedActionIds = [];

    this.previousEdges = null;
    this.previousActionIds = null;

    // Run this after the transition has finished, otherwise the
    // should-animate-nearby class on VWB Nodes will interfere with
    // ember-animated animations
    // We also run this a little later, because otherwise we
    // run into weird glitches when the state transitions before the animations
    // have really finished
    scheduleOnce('afterRender', this, () => {
      later(
        this,
        () => {
          this.isSelectingDropAreaThroughClick = true;
          this.isCopyingHighlightedActions = true;
        },
        3 * this.transitionDuration,
      );
    });

    if (this.args.onUndo) {
      this.args.onUndo();
    }
  });

  @tracked isShowingCopyFromModal = false;
  @tracked isShowingCopyToModal = false;

  @action
  startCopyFrom() {
    this.analytics.track('campaign', 'workflow_existing_item_copy_from_started', {
      model: this.args.campaign,
    });

    this.isShowingCopyFromModal = true;
  }

  @action
  closeCopyFromModal() {
    this.isShowingCopyFromModal = false;

    this.clearTemporaryCampaignsAndActions();
  }

  @action
  closeCopyToModal() {
    this.isShowingCopyToModal = false;

    this.clearTemporaryCampaignsAndActions();
  }

  @action
  clearTemporaryCampaignsAndActions() {
    // The "copy from" and "copy to" modals create temporary models in the Ember
    // Data store, since they render the VWB, and a lot of its internals rely on
    // Campaign and Action model functionality. We need to make sure to clear
    // those entries when the modals close.
    this.store
      .peekAll('campaign')
      .filter((c) => c._copy)
      .forEach((c) => c.unloadRecord());
    this.store
      .peekAll('action')
      .filter((a) => a._copy)
      .forEach((a) => a.unloadRecord());
  }

  @tracked copyFromEnvironmentId: string | null = null;
  @action
  confirmCopyFromModalSelection(environmentId: string, campaign: CampaignModel, highlightedNodeIds: string[]) {
    this.copyFromEnvironmentId = environmentId;
    this.copyFromCampaign = campaign;
    this.args.updateHighlightedNodeIds(highlightedNodeIds);
    this.isShowingCopyFromModal = false;
    this.isSelectingDropAreaThroughClick = true;
    this.isCopyingHighlightedActions = true;
  }

  @action
  toggleHighlightedNode(actionModel: ActionModel) {
    let nodeId = actionModel.id;
    // First, check whether any of the clicked action's parent branches (if the
    // action is nested) are already highlighted.
    // If so, don't toggle the current node - nested nodes can't be selected or
    // deselected when the parent branch is already highlighted (a branch action
    // can only be highlighted as a whole).
    let parentBranchIds = this.args.campaign.parentBranchChains[nodeId] ?? [];

    let currentlyHighlightedNodeIds = this.isShiftHeld ? [...this.externalHighlightedNodeIds] : [];

    if (parentBranchIds.length > 0) {
      let isAnyParentBranchHighlighted = currentlyHighlightedNodeIds.some((id) => parentBranchIds.includes(id));

      if (isAnyParentBranchHighlighted) {
        return;
      }
    }

    let newHighlightedNodes: string[] = [];

    let relevantNodeIds = [nodeId];

    let isBranch = !!this.args.campaign.branchesMap[nodeId];

    // If the action in question is a branch action, also toggle all its
    // descendants
    if (isBranch) {
      let branchActionModel = this.args.campaign.branchesMap[nodeId]?.action;

      if (branchActionModel) {
        relevantNodeIds = [nodeId, ...branchActionModel.subActions];
      }
    }

    if (currentlyHighlightedNodeIds.includes(nodeId)) {
      newHighlightedNodes = currentlyHighlightedNodeIds.filter((id) => !relevantNodeIds.includes(id));
    } else {
      newHighlightedNodes = [...currentlyHighlightedNodeIds, ...relevantNodeIds];
    }

    this.args.updateHighlightedNodeIds(newHighlightedNodes.uniq());
    this.hasChangedHighlight(actionModel);
  }

  @action
  onActionClicked(event: MouseEvent, actionModel: ActionModel) {
    if (this.args.isMinimapMode === true && actionModel.isMessagingType) {
      this.router.transitionTo('composer.action', actionModel.id);
      this.analytics.track('message', 'minimap_clicked', {
        currentAction: this.args.campaign.actions.findBy('id', this.args.selectedMinimapAction),
        clickedAction: actionModel,
      });
      return;
    }

    if (actionModel.isVariation) {
      return;
    }

    if (this.isSaving) {
      return;
    }

    // Reset shift state based on other events, because sometimes a
    // keyDown/keyUp may not be caught
    if (this.isShiftHeld && !event.shiftKey) {
      this.isShiftHeld = false;
    }

    // The click event may get defaultPrevented if a drag action has been
    // started on the workflow action component, and the mouseup also ended on
    // the same container. In this case, we don't want to call an onclick,
    // because the intention was a drag.
    if (event.defaultPrevented) {
      return;
    }

    // If we're in scroll mode, don't toggle when clicking
    if (this.mouseMode === MouseMode.Scroll) {
      return;
    }

    // We don't allow highlighting exits
    if (actionModel.isExit) {
      return;
    }

    if (
      this.hasMovedMouse ||
      this.isMovingHighlightedActions ||
      this.isCopyingHighlightedActions ||
      this.isPerformingDragOperation ||
      (!this.args.isCopyingFrom && this.args.isReadOnly) ||
      this.args.isCampaignReview
    ) {
      return;
    }

    this.toggleHighlightedNode(actionModel);
  }

  addOrMoveActionTask = task(
    { drop: true },
    async (
      dropType: DropType,
      dropItem: any,
      event: FlyDragEvent,
      meta?: { branch?: any; branchOutgoingEdges?: any; copyFromEnv: string; copy: boolean; newEdges?: Edge[] },
    ) => {
      let beforeActionIds = [...this.args.campaign.workflowActionIds];
      let beforeEdges = [...this.args.campaign.edges];

      this.isPerformingDragOperation = false;

      await this.args.addActionTask.perform(dropType, dropItem, event, meta as any);
      let afterActionIds = [...this.args.campaign.workflowActionIds];

      // After adding a new action, update highlighted nodes - it's possible
      // that an action was added within a currently highlighted branch. If so,
      // we need to make sure that action becomes highlighted
      this.args.updateHighlightedNodeIds(
        this.externalHighlightedNodeIds.reduce(reduceWithBranchChildren.bind(this, this.args.campaign), [] as string[]),
      );

      this.justAddedActionIds = afterActionIds.filter((id) => !beforeActionIds.includes(id));
      this.previousActionIds = beforeActionIds;
      this.previousEdges = beforeEdges;
    },
  );

  @tracked bulkMoveDelayActionIds: string[] = [];
  @tracked bulkMoveTotalDelaySubjectsCount = 0;

  maybeStartBulkMoveTask = task({ drop: true }, async (dropItem: DropTargetItem, dropType: DropType) => {
    this.isMovingHighlightedActions = false;

    let delayActionIds = this.externalHighlightedNodeIds.filter((id) => this.store.peekRecord('action', id)?.isDelay);
    let totalSubjectsCount = delayActionIds
      .map((id) => this.actionSubjects.find(id))
      .filter((item) => (item?.count ?? 0) > 0)
      .reduce((sum, item) => sum + (item?.count ?? 0), 0);

    if (totalSubjectsCount > 0) {
      this.resetNodeTranslations();

      this.bulkMoveDelayActionIds = delayActionIds;
      this.bulkMoveTotalDelaySubjectsCount = totalSubjectsCount;

      await waitForEvent(this.eventBus, VWB_CONFIRM_BULK_MOVE);
    }

    this.moveHighlightedActions.perform(dropItem, dropType);

    this.bulkMoveDelayActionIds = [];
    this.bulkMoveTotalDelaySubjectsCount = 0;
  });

  @action
  cancelBulkMove() {
    this.maybeStartBulkMoveTask.cancelAll({ resetState: true });
    this.eventBus.trigger(VWB_CANCEL_MOVE);
    this.bulkMoveDelayActionIds = [];
    this.bulkMoveTotalDelaySubjectsCount = 0;
  }

  @action
  confirmStartBulkMove() {
    this.eventBus.trigger(VWB_CONFIRM_BULK_MOVE);
  }

  @tracked dragStartEvent: FlyDragEvent | null = null;

  @action
  onDragStart(event: FlyDragEvent) {
    this.dragStartEvent = event;

    if (event.target.dataset.id) {
      let copyHighlighted =
        this.dragMode === DragMode.Copy &&
        this.externalHighlightedNodeIds.length > 0 &&
        this.externalHighlightedNodeIds.includes(event.target.dataset.id);
      let moveHighlighted =
        this.dragMode === DragMode.Move &&
        this.externalHighlightedNodeIds.length > 0 &&
        this.externalHighlightedNodeIds.includes(event.target.dataset.id);

      if (copyHighlighted) {
        this.isCopyingHighlightedActions = true;
      } else if (moveHighlighted) {
        this.isMovingHighlightedActions = true;
      }
    }

    this.isPerformingDragOperation = true;
  }

  @action
  onDragEnd() {
    this.dragStartEvent = null;

    if (!this.isSelectingDropAreaThroughClick) {
      this.isMovingHighlightedActions = false;
      this.isCopyingHighlightedActions = false;
    }

    // Alt may have been released during the drag, but we don't remove the
    // "copy" cursor while a copy drag is happening. Reset the state in such
    // cases
    if (!this.isAltHeld) {
      this.changeDragMode(DragMode.Move);
    }

    this.isPerformingDragOperation = false;
  }

  @action
  resetNodeTranslations() {
    let wereAnyNodesTranslated = false;

    Object.values(this.currentGraph?.nodes ?? {}).forEach((node) => {
      if (node.translateX !== 0 || node.translateY !== 0) {
        wereAnyNodesTranslated = true;

        setProperties(node, {
          translateX: 0,
          translateY: 0,
        });
      }
    });

    let resetState = () => {
      this.isSelectingDropAreaThroughClick = false;
      this.isPerformingDragOperation = false;
    };

    if (wereAnyNodesTranslated) {
      later(resetState, this.transitionDuration);
    } else {
      resetState();
    }
  }

  @tracked actionBeingMoved: ActionModel | null = null;

  @action
  onDropOrClickDropArea(
    dropItem: DropTargetItem,
    dropType: DropType,
    // draggable and droppable are (usually) Component instances, but we'll
    // type them as any because it can be more than one specific component, so
    // it's just easier to be super generic
    event: FlyDragEvent,
    _meta?: { branch?: any; branchOutgoingEdges?: any; copyFromEnv: string; copy: boolean; newEdges?: Edge[] },
  ) {
    this.notifyAddMoveOrDelete();

    // We're inside the "Copy to" modal and selecting where to drop items
    if (this.args.isCopyingTo && this.args.copyToSourceCampaign && this.args.copyToTargetEnvironmentId) {
      return this.copyHighlightedActions.perform(
        this.args.copyToSourceCampaign,
        this.args.campaign,
        this.environment.current.id ?? '',
        this.args.copyToTargetEnvironmentId,
        this.args.highlightedNodeIds,
        dropItem,
        dropType,
        true,
      );
    }

    // We have selected items from a "Copy from" modal and are now placing them
    // in the current campaign
    if (this.copyFromCampaign && this.copyFromEnvironmentId) {
      let trackActions = this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id));
      let sameEnvironment = this.copyFromEnvironmentId === this.environment.current.id ?? '';

      return this.copyHighlightedActions.perform(
        this.copyFromCampaign,
        this.args.campaign,
        this.copyFromEnvironmentId,
        this.environment.current.id ?? '',
        this.externalHighlightedNodeIds,
        dropItem,
        dropType,
        false,
        () => {
          this.analytics.track('campaign', 'workflow_existing_item_copy_from_completed', {
            model: this.args.campaign,
            actions: trackActions,
            trayTab: this.args.tab,
            sameEnvironment,
          });
        },
      );
    }

    // We are copying items within the same campaign
    if (this.isCopyingHighlightedActions) {
      let initiationType = this.isSelectingDropAreaThroughClick ? 'action menu' : 'keyboard';
      let trackActions = this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id));

      return this.copyHighlightedActions.perform(
        this.args.campaign,
        this.args.campaign,
        this.environment.current.id ?? '',
        this.environment.current.id ?? '',
        this.externalHighlightedNodeIds,
        dropItem,
        dropType,
        false,
        () => {
          this.analytics.track('campaign', 'workflow_existing_item_copy_completed', {
            model: this.args.campaign,
            actions: trackActions,
            trayTab: this.args.tab,
            initiationType,
          });
        },
      );
    }

    // We are ALT dragging a single action
    if (this.dragMode === DragMode.Copy && event.detail.context.draggable?.action?.id) {
      let actionIdsToCopy = [event.detail.context.draggable.action.id];

      let actionModel = event.detail.context.draggable.action;
      // If the action we're alt-dragging is a branch action, also copy all its
      // contents
      if (actionModel?.isBranch) {
        actionIdsToCopy = [...actionIdsToCopy, ...actionModel.subActions];
      }

      let trackActions = actionIdsToCopy.map((id) => this.store.peekRecord('action', id));

      return this.copyHighlightedActions.perform(
        this.args.campaign,
        this.args.campaign,
        this.environment.current.id ?? '',
        this.environment.current.id ?? '',
        actionIdsToCopy,
        dropItem,
        dropType,
        false,
        () => {
          this.analytics.track('campaign', 'workflow_existing_item_copy_completed', {
            model: this.args.campaign,
            actions: trackActions,
            trayTab: this.args.tab,
            initiationType: 'keyboard',
          });
        },
      );
    }

    // We are moving multiple items within the same campaign
    if (this.isMovingHighlightedActions) {
      return this.maybeStartBulkMoveTask.perform(dropItem, dropType);
    }

    // We are moving a single existing action
    if (event.detail.context.draggable?.action) {
      return this.maybeStartSingleMoveTask.perform(dropItem, dropType, event);
    }

    // // We are adding a new action
    return this.addOrMoveActionTask.perform(dropType, dropItem, event);
  }

  @tracked singleMoveDelayActionIds: string[] = [];
  @tracked singleMoveTotalDelaySubjectsCount = 0;

  maybeStartSingleMoveTask = task(
    { drop: true },
    async (dropItem: DropTargetItem, dropType: DropType, event: FlyDragEvent) => {
      let actionModel = event.detail.context.draggable?.action as ActionModel;
      if (!actionModel) {
        return;
      }

      let isDelayOrContainsDelays = false;
      let delaysBeingMoved: ActionModel[] = [];

      // For branches that have delay actions within them, we want to confirm that a user wants to move
      if (actionModel.isBranch) {
        let childDelayActions = actionModel.subActions
          .map((id) => this.store.peekRecord('action', id))
          .filter((a) => a?.isDelay) as ActionModel[];

        if (childDelayActions.length > 0) {
          isDelayOrContainsDelays = true;
          delaysBeingMoved = childDelayActions;
        }
      }

      // For single delay-actions, we also want to confirm that a user wants to move
      if (actionModel.isDelay) {
        isDelayOrContainsDelays = true;
        delaysBeingMoved = [actionModel];
      }

      if (isDelayOrContainsDelays) {
        let subjectsCount = delaysBeingMoved
          .map((action) => this.actionSubjects.find(action.id))
          .filter((item) => (item?.count ?? 0) > 0)
          .reduce((sum, item) => sum + (item?.count ?? 0), 0);

        if (subjectsCount > 0) {
          this.actionBeingMoved = actionModel;
          this.resetNodeTranslations();

          this.singleMoveDelayActionIds = delaysBeingMoved.map((a) => a.id);
          this.singleMoveTotalDelaySubjectsCount = subjectsCount;

          await waitForEvent(this.eventBus, VWB_CONFIRM_SINGLE_MOVE);
        }
      }

      this.addOrMoveActionTask.perform(dropType, dropItem, event);

      this.singleMoveDelayActionIds = [];
      this.singleMoveTotalDelaySubjectsCount = 0;
    },
  );

  @action
  confirmStartSingleMove() {
    this.eventBus.trigger(VWB_CONFIRM_SINGLE_MOVE);
  }

  @action
  cancelSingleMove() {
    this.eventBus.trigger(VWB_CANCEL_MOVE);
    this.maybeStartSingleMoveTask.cancelAll({ resetState: true });
    this.actionBeingMoved = null;
    this.singleMoveDelayActionIds = [];
    this.singleMoveTotalDelaySubjectsCount = 0;
  }

  @action
  trackCopyModalActionCompletedEvent(
    copyFrom: boolean,
    modalActionType: WorkflowCopyModalActions,
    optionalData?: { actions?: ActionModel[] },
  ) {
    let eventName = copyFrom ? WorkflowCopyModalAnalyticsEvents.CopyFrom : WorkflowCopyModalAnalyticsEvents.CopyTo;

    let data: any = {
      model: this.args.campaign,
      actions: [],
      modalActionType,
    };

    if (copyFrom && optionalData) {
      data = { ...data, ...optionalData };
    }

    if (!copyFrom) {
      data.actions = this.currentlyHighlightedNodeIds.map((id) => this.store.peekRecord('action', id));
    }

    this.analytics.track('campaign', eventName, data);
  }

  // Enable "in-viewport" functionality in individual nodes for larger campaigns
  get trackInViewport() {
    return this.args.campaign.workflowActions.length > IN_VIEWPORT_CAMPAIGN_SIZE_THRESHOLD;
  }

  @action
  notifyAddMoveOrDelete() {
    this.eventBus.publish(VWB_BEFORE_ACTION_ADD_MOVE_OR_DELETE);
  }

  @computed('auth.user.userFlags.aggressive_positivity_available')
  get isAggressivePositivityAvailable() {
    if (this.args.isCampaignReview) {
      return false;
    }

    return this.auth.user?.userFlags?.aggressive_positivity_available ?? false;
  }

  get demoMode() {
    return this.args.demoMode ?? false;
  }
}
